import * as React from 'react';
import {connect} from 'react-redux';

import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {NutshellState} from '../../../store';
import {writeLocalStorage, readLocalStorage} from '../../../util/localStorage';

import {getIsOpen} from '../../email/email-composer-selectors';

import {PeepTimelineCreatorForm} from './peep-timeline-creator-form';
import {TimelineCreatorEmail} from './timeline-creator-email';
import {TimelineCreatorTabs} from './timeline-creator-tabs';

import styles from './timeline-creator.module.css';

export type ContentType = 'ACTIVITY' | 'NOTE';
export const TIMELINE_CREATOR_CONTAINER_ID = 'timeline-creator-container';

function mapStateToProps(state: NutshellState): StateProps {
    return {
        isOpen: getIsOpen(state),
    };
}

type OwnProps = {
    entityId: string;
    entityName: string;
    entityPageName: string | null | undefined;
    refetchEvents: () => Promise<any>;
};

type StateProps = {
    isOpen: boolean;
};

type Props = OwnProps & StateProps;

const LAST_SELECTED_TIMELINE_TAB_KEY = 'LAST_SELECTED_TIMELINE_TAB';

function TimelineCreatorComponent(props: Props) {
    const [selectedTab, setSelectedTab] = React.useState<ContentType>(
        (readLocalStorage(LAST_SELECTED_TIMELINE_TAB_KEY) as ContentType) || 'ACTIVITY'
    );
    const [showEmail, setShowEmail] = React.useState<boolean>(false);
    const [shouldAutoFocusEditor, setShouldAutoFocusEditor] = React.useState<boolean>(false);

    const hasFooterBar = selectedTab === 'NOTE' || selectedTab === 'ACTIVITY';

    const {isOpen} = props;

    // refetch events if the modal isOpen state changes to closed
    useDidMountEffect(() => {
        if (!isOpen) {
            props.refetchEvents();
        }
    }, [isOpen]);

    return (
        <div
            id={TIMELINE_CREATOR_CONTAINER_ID}
            style={{minHeight: hasFooterBar ? '240px' : '180px'}}
            className={styles['timeline-creator']}
        >
            <div className={styles.tabs}>
                <TimelineCreatorTabs
                    entityId={props.entityId}
                    entityPageName={props.entityPageName}
                    selectedTab={selectedTab}
                    onChange={(tab: ContentType) => {
                        setSelectedTab(tab);
                        // Autofocus editor when user changes tabs
                        setShouldAutoFocusEditor(true);

                        writeLocalStorage(LAST_SELECTED_TIMELINE_TAB_KEY, tab);
                    }}
                    onShowEmail={() => {
                        setShowEmail(true);
                    }}
                />
            </div>
            {showEmail && (
                <TimelineCreatorEmail
                    entityId={props.entityId}
                    postModalOpen={() => {
                        setShouldAutoFocusEditor(false);
                        setShowEmail(false); // reset the showEmail state so the modal can be reopened
                    }}
                />
            )}
            <PeepTimelineCreatorForm
                entityId={props.entityId}
                entityName={props.entityName}
                refetchEvents={props.refetchEvents}
                contentType={selectedTab}
                shouldAutoFocusEditor={shouldAutoFocusEditor}
                isFullWidth={true}
            />
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const TimelineCreator = connector(TimelineCreatorComponent);
